/**
 * Crop a picture before it's saved (or crop one already saved): a modal
 * with the picture and a frame to drag (move) or resize from its corners.
 * `crop(pic)` resolves with the cropped picture, the whole one ("Use whole
 * picture"), or null when cancelled. Used for mockups, map sections and
 * placement pictures (Doreen, 2026-10-22).
 */
import { useEffect, useRef, useState } from 'react';
import { Button } from '../ui/Button';
import { Modal } from '../ui/Modal';
import { toJpeg } from '../../lib/api/placementMap';

export interface Pic { image: string; width: number; height: number }
type Box = { x: number; y: number; w: number; h: number }; // percent of the picture
type Mode = 'move' | 'nw' | 'ne' | 'sw' | 'se';

const clamp = (v: number, lo: number, hi: number) => Math.min(Math.max(v, lo), hi);

const loadImage = (src: string) =>
  new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Could not open the picture.'));
    img.src = src;
  });

/** Cut `box` (percent) out of the picture, as a JPEG data URL (as sharp as fits). */
const cut = async (pic: Pic, box: Box): Promise<Pic> => {
  const img = await loadImage(pic.image);
  const W = img.naturalWidth;
  const H = img.naturalHeight;
  const sx = Math.round((box.x / 100) * W);
  const sy = Math.round((box.y / 100) * H);
  const sw = Math.max(1, Math.round((box.w / 100) * W));
  const sh = Math.max(1, Math.round((box.h / 100) * H));
  const canvas = document.createElement('canvas');
  canvas.width = sw;
  canvas.height = sh;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Could not crop the picture.');
  ctx.fillStyle = '#fff';
  ctx.fillRect(0, 0, sw, sh);
  ctx.drawImage(img, sx, sy, sw, sh, 0, 0, sw, sh);
  return toJpeg(canvas);
};

export function useCropper() {
  const [pic, setPic] = useState<Pic | null>(null);
  const [box, setBox] = useState<Box>({ x: 0, y: 0, w: 100, h: 100 });
  const [busy, setBusy] = useState(false);
  const resolver = useRef<((p: Pic | null) => void) | null>(null);
  const area = useRef<HTMLDivElement>(null);
  const drag = useRef<{ mode: Mode; sx: number; sy: number; start: Box; rect: DOMRect } | null>(null);

  const crop = (p: Pic) =>
    new Promise<Pic | null>((resolve) => {
      resolver.current = resolve;
      setBox({ x: 0, y: 0, w: 100, h: 100 });
      setPic(p);
    });

  const finish = (result: Pic | null) => {
    resolver.current?.(result);
    resolver.current = null;
    setPic(null);
  };

  useEffect(() => {
    const move = (e: PointerEvent) => {
      const d = drag.current;
      if (!d) return;
      const dx = ((e.clientX - d.sx) / d.rect.width) * 100;
      const dy = ((e.clientY - d.sy) / d.rect.height) * 100;
      const s = d.start;
      let b = { ...s };
      if (d.mode === 'move') {
        b = { ...s, x: clamp(s.x + dx, 0, 100 - s.w), y: clamp(s.y + dy, 0, 100 - s.h) };
      } else {
        const left = d.mode === 'nw' || d.mode === 'sw';
        const top = d.mode === 'nw' || d.mode === 'ne';
        if (left) { const x = clamp(s.x + dx, 0, s.x + s.w - 3); b.x = x; b.w = s.x + s.w - x; }
        else b.w = clamp(s.w + dx, 3, 100 - s.x);
        if (top) { const y = clamp(s.y + dy, 0, s.y + s.h - 3); b.y = y; b.h = s.y + s.h - y; }
        else b.h = clamp(s.h + dy, 3, 100 - s.y);
      }
      setBox(b);
    };
    const up = () => { drag.current = null; };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    return () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
  }, []);

  const start = (mode: Mode) => (e: React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!area.current) return;
    drag.current = { mode, sx: e.clientX, sy: e.clientY, start: box, rect: area.current.getBoundingClientRect() };
  };

  const apply = async () => {
    if (!pic) return;
    setBusy(true);
    try {
      finish(box.w > 99.5 && box.h > 99.5 ? pic : await cut(pic, box));
    } finally {
      setBusy(false);
    }
  };

  const px = pic ? { w: Math.round((box.w / 100) * pic.width), h: Math.round((box.h / 100) * pic.height) } : null;
  const handle = (m: Mode, style: React.CSSProperties) => (
    <span
      onPointerDown={start(m)}
      style={{ position: 'absolute', width: 14, height: 14, background: '#fff', border: '2px solid var(--sitewide)', borderRadius: 3, ...style }}
    />
  );

  const element = pic ? (
    <Modal
      open
      onClose={() => finish(null)}
      title="Crop the picture"
      maxWidth={960}
      footer={
        <>
          <span style={{ fontSize: 12, color: 'var(--ink-4)', marginRight: 'auto' }}>{px ? `${px.w} × ${px.h} px` : ''}</span>
          <Button variant="ghost" onClick={() => finish(null)}>Cancel</Button>
          <Button variant="ghost" onClick={() => finish(pic)}>Use whole picture</Button>
          <Button variant="primary" onClick={apply} disabled={busy}>{busy ? 'Cropping…' : 'Crop'}</Button>
        </>
      }
    >
      <div style={{ fontSize: 12.5, color: 'var(--ink-4)', marginBottom: 10 }}>
        Drag the frame to move it, or its corners to resize. Everything outside the frame is cut off.
      </div>
      <div style={{ maxHeight: '68vh', overflow: 'auto', display: 'flex', justifyContent: 'center', background: 'var(--bg-subtle)', borderRadius: 8, padding: 8 }}>
        <div ref={area} style={{ position: 'relative', display: 'inline-block', lineHeight: 0, userSelect: 'none', overflow: 'hidden' }}>
          <img src={pic.image} alt="" draggable={false} style={{ maxWidth: '100%', maxHeight: '64vh', display: 'block' }} />
          <div
            onPointerDown={start('move')}
            style={{
              position: 'absolute', left: `${box.x}%`, top: `${box.y}%`, width: `${box.w}%`, height: `${box.h}%`,
              border: '2px solid var(--sitewide)', boxShadow: '0 0 0 9999px rgba(20,19,15,0.45)', cursor: 'move',
            }}
          >
            {handle('nw', { left: -2, top: -2, cursor: 'nwse-resize' })}
            {handle('ne', { right: -2, top: -2, cursor: 'nesw-resize' })}
            {handle('sw', { left: -2, bottom: -2, cursor: 'nesw-resize' })}
            {handle('se', { right: -2, bottom: -2, cursor: 'nwse-resize' })}
          </div>
        </div>
      </div>
    </Modal>
  ) : null;

  return { crop, element };
}
